/**
 * #445-ws2 — the OPERATOR-ASSESSED Response Level overlay for the SCORED anomaly families
 * (flood + ice-storm). Pure functions so the disc layer, the queue converter, and the detail
 * body all read the SAME reconciliation (no React/Cesium; unit-testable).
 *
 * A flood / ice anomaly ALWAYS carries a system-derived `response_level` (1..4); an owning IC
 * may pin an operator-assessed level on the incident it was Declared into. The two are NEVER
 * conflated — the derived is preserved — and reconciled exactly as the fire/hotspot side:
 *   • the DISC + QUEUE row read the ESCALATION-SAFE max (an IC escalation reddens/lifts them;
 *     an IC de-escalation can never bury a worse live derived — the life-safety guard), and
 *   • the DETAIL body reads the OPERATIVE (effective = operator ?? derived) shown ALONGSIDE the
 *     annotated system-derived (honesty), which the AnomalyResponseLevelBadge renders.
 * See packages/shared-types/src/response-level.ts for the shared math + rationale.
 */
import { escalationSafeResponseLevel } from '@tsr/shared-types';

/** The overlay + derived carried by a flood / ice anomaly feature's properties. */
export interface AnomalyResponseLevelProps {
  operator_response_level?: number | null;
  response_level: number;
}

/**
 * The ESCALATION-SAFE Response Level that drives the flood/ice map DISC + urgent QUEUE row:
 * the owning IC's operator pin layered OVER the derived `response_level` via the never-bury max.
 * `operator_response_level` is null for a non-owned anomaly ⇒ the pure derived (disc/queue
 * unchanged). Mirrors ActiveFiresLayer.effectiveFireResponseLevel (which nulls a non-OC fire;
 * a flood/ice anomaly is always scored, so the derived is the floor here).
 */
export function escalationSafeAnomalyResponseLevel(p: AnomalyResponseLevelProps): number {
  return (
    escalationSafeResponseLevel(p.operator_response_level ?? null, p.response_level) ?? p.response_level
  );
}
